import XLSX from 'xlsx';
import { resolveDepartmentTabs } from './constants';
import { formatWorkWeekday } from './format';

const safeSheetName = name =>
  String(name)
    .replace(/[\\/?*[\]]/g, '_')
    .slice(0, 31) || 'DEPT';

const formatMultiplier = label => {
  if (!label) return '';
  return String(label).replace(/×/g, 'x');
};

const round2 = n => Math.round((Number(n) || 0) * 100) / 100;

/** Excel serial date from ISO YYYY-MM-DD (UTC noon, Excel 1900 epoch). */
const isoToExcelDate = isoDate => {
  if (!isoDate) return null;
  const parts = String(isoDate).split('-').map(Number);
  if (parts.length < 3 || parts.some(Number.isNaN)) return null;
  const utc = Date.UTC(parts[0], parts[1] - 1, parts[2]);
  const epoch = Date.UTC(1899, 11, 30);
  return (utc - epoch) / 86400000;
};

/** Excel time serial from HH:mm (fraction of day). */
const timeToExcelSerial = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const minutes = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  if (Number.isNaN(minutes)) return null;
  return minutes / (24 * 60);
};

const applyDepartmentDateTimeFormats = (sheet, rowCount, headers) => {
  const col = name => headers.indexOf(name);
  const dateCol = col('DATE');
  const startCol = col('START');
  const endCol = col('END');

  for (let r = 1; r <= rowCount; r += 1) {
    if (dateCol >= 0) {
      const addr = XLSX.utils.encode_cell({ r, c: dateCol });
      const cell = sheet[addr];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = 'dd/mm/yyyy';
      }
    }
    if (startCol >= 0) {
      const addr = XLSX.utils.encode_cell({ r, c: startCol });
      const cell = sheet[addr];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = 'hh:mm';
      }
    }
    if (endCol >= 0) {
      const addr = XLSX.utils.encode_cell({ r, c: endCol });
      const cell = sheet[addr];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = 'hh:mm';
      }
    }
  }
};

const DEPT_HEADERS = [
  'DAY',
  'DATE',
  'ENGINEER',
  'CATEGORY',
  'CUSTOMER',
  'JOB DESCRIPTION',
  'START',
  'END',
  'RAW DURATION (mins)',
  'PAYABLE (mins)',
  'MULTIPLIER',
  'TOTALHRS',
  'ON-CALL(Y/N)',
  'ON-CALL ALLOWANCE (£)',
  'EXPENSES(£)',
  'MILEAGE(miles)',
  'MILEAGE DEDUCTION(£)',
  'KIT TIME',
  'NOTES',
];

const buildDepartmentSheet = rows => {
  const aoa = [DEPT_HEADERS];

  (rows || []).forEach(e => {
    const excelDate = isoToExcelDate(e.date);
    const startVal = timeToExcelSerial(e.startTime);
    const endVal = timeToExcelSerial(e.endTime);

    aoa.push([
      e.day || formatWorkWeekday(e.date),
      excelDate != null ? excelDate : '',
      e.engineerName || e.engineerId,
      e.categoryLabel || e.category || '',
      e.customerName || '',
      e.jobDescription || '',
      startVal != null ? startVal : '',
      endVal != null ? endVal : '',
      e.rawDurationMinutes == null ? '' : e.rawDurationMinutes,
      e.payableMinutes == null ? '' : e.payableMinutes,
      formatMultiplier(e.multiplier),
      e.totalHrs == null ? '' : e.totalHrs,
      e.onCallY ? 'Y' : 'N',
      e.onCallAllowance == null ? '' : e.onCallAllowance,
      '',
      '',
      '',
      e.kitCollectionTime || '',
      e.notes || '',
    ]);
  });

  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  if (rows && rows.length) {
    applyDepartmentDateTimeFormats(sheet, rows.length, DEPT_HEADERS);
  }
  return sheet;
};

const toMasterRow = row => ({
  NAME: row.engineerName || row.engineerId,
  TEAM: row.department || '',
  STANDARDHRS: row.standardHrs || 0,
  'SICKNESS(hrs)': row.sicknessHrs || 0,
  UNPAIDHRS: row.unpaidHrs || 0,
  'HOLIDAY(hrs)': row.holidayHrs || 0,
  'OVERTIME 1.5x (hrs)': row.overtime15Hrs || 0,
  'DOUBLE TIME 2.0x (hrs)': row.overtime20Hrs || 0,
  'STANDBY /ON-CALL (£)': row.standbyOnCallPounds || 0,
  'SALESCOMMISSION(£)': row.salesCommission == null ? '' : row.salesCommission,
  'EXPENSES(£)': '',
  'MILEAGE(miles)': '',
  'MILEAGE DEDUCTION(£)': '',
  'OTHERDEDUCTION (£)':
    row.otherDeductionPounds == null ? '' : row.otherDeductionPounds,
});

const numericMasterKeys = [
  'STANDARDHRS',
  'SICKNESS(hrs)',
  'UNPAIDHRS',
  'HOLIDAY(hrs)',
  'OVERTIME 1.5x (hrs)',
  'DOUBLE TIME 2.0x (hrs)',
  'STANDBY /ON-CALL (£)',
];

/** Build MASTER + department sheets for CSS payroll export. */
export const buildTimesheetWorkbook = (master, departments, departmentOrder) => {
  const wb = XLSX.utils.book_new();

  const masterRows = (master || []).map(toMasterRow);
  if (masterRows.length) {
    const totals = {
      NAME: 'TOTAL',
      TEAM: '',
      'SALESCOMMISSION(£)': '',
      'OTHERDEDUCTION (£)': '',
    };
    numericMasterKeys.forEach(key => {
      totals[key] = round2(
        masterRows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0),
      );
    });
    masterRows.push(totals);
  }

  const masterSheet = XLSX.utils.json_to_sheet(masterRows);
  XLSX.utils.book_append_sheet(wb, masterSheet, 'MASTER');

  const deptNames =
    departmentOrder && departmentOrder.length
      ? departmentOrder
      : resolveDepartmentTabs(departments);

  deptNames.forEach(dept => {
    const rows = departments[dept] || [];
    const sheet = buildDepartmentSheet(rows);
    XLSX.utils.book_append_sheet(wb, sheet, safeSheetName(dept));
  });

  return wb;
};

export const timesheetExportFilename = (stamp = null) => {
  const date =
    stamp ||
    (() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    })();
  return `CSS_Timesheet_Export_${date}.xlsx`;
};

/** Trigger browser download of the export workbook. */
export const downloadTimesheetWorkbook = (
  master,
  departments,
  stamp,
  departmentOrder,
) => {
  const wb = buildTimesheetWorkbook(master, departments, departmentOrder);
  XLSX.writeFile(wb, timesheetExportFilename(stamp));
};
